import { Box, ListItem, StackDivider, Text, UnorderedList, VStack } from '@chakra-ui/react';
import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Callout } from 'components/Callout';
import { LegalSection } from 'components/LegalSection/LegalSection';
import { LegalBlock, LEGAL_SECTIONS } from './legalSectionsData';

const PRIMARY = 'var(--color-primary-500, #FF5C4F)';
const TEXT_PRIMARY = 'var(--text-primary, #1B1917)';

/** `<b>` segments render bold + darker inside body prose. */
const bodyBold = {
  b: (chunks: ReactNode) => (
    <Box as='span' fontWeight='700' color={TEXT_PRIMARY}>
      {chunks}
    </Box>
  ),
};

/** `<b>` segments inside a callout keep the callout's own text color. */
const calloutBold = {
  b: (chunks: ReactNode) => (
    <Box as='span' fontWeight='700'>
      {chunks}
    </Box>
  ),
};

const renderBlock = (block: LegalBlock, index: number) => {
  if (block.kind === 'list') {
    return (
      <Box key={index}>
        {block.label && (
          <Text as='p' m={0} mb='8px' fontWeight='700' color={TEXT_PRIMARY}>
            <FormattedMessage {...block.label} />
          </Text>
        )}
        <UnorderedList m={0} ps='1.25em' spacing='6px'>
          {block.items.map((item, i) => (
            <ListItem key={i} sx={{ '&::marker': { color: PRIMARY } }}>
              <FormattedMessage {...item} />
            </ListItem>
          ))}
        </UnorderedList>
      </Box>
    );
  }

  return (
    <Text key={index} as='p' m={0}>
      <FormattedMessage {...block.text} values={bodyBold} />
    </Text>
  );
};

/** Renders all 7 legal sections from {@link LEGAL_SECTIONS} — data-driven, no per-section markup. */
export const LegalSections = () => (
  <VStack
    as='main'
    align='stretch'
    spacing={{ base: '28px', md: '36px' }}
    divider={<StackDivider borderColor='#E8E5E2' />}
  >
    {LEGAL_SECTIONS.map((section) => (
      <LegalSection
        key={section.number}
        number={section.number}
        title={<FormattedMessage {...section.title} />}
        body={
          <VStack align='stretch' spacing={{ base: '10px', md: '12px' }}>
            {section.body.map(renderBlock)}
          </VStack>
        }
        callouts={section.callouts?.map((callout, i) => (
          <Callout key={i} status={callout.status}>
            <FormattedMessage {...callout.text} values={calloutBold} />
          </Callout>
        ))}
      />
    ))}
  </VStack>
);
